import { API } from '@/api'
import {
  FOLLOW_TASK_PLATFORM,
  FollowTaskInfo,
  SOCIAL_BIND_KEYS,
  SOCIAL_MEDIA_DISCORD_ENABLED,
  SOCIAL_MEDIA_MOCK_CREDIT_REWARD,
  SocialBindStatus,
  SocialPlatform,
} from '@/models/social-media'
import { getToken } from '@/utils/auth'
import { API_BASE_URL } from '@/utils/http'

export const OAUTH_BIND_POLL_INTERVAL_MS = 2000
export const OAUTH_BIND_POLL_MAX_MS = 5 * 60 * 1000

const PLATFORMS: SocialPlatform[] = [
  'x',
  ...(SOCIAL_MEDIA_DISCORD_ENABLED ? (['discord'] as const) : []),
]

/** 100 cents = 1 credit (same as billing) */
export function rewardAmountCentToCredits(rewardAmountCent: number): number {
  return rewardAmountCent / 100
}

function resolveCreditEarned(linked: boolean, followTask?: FollowTaskInfo): number | undefined {
  if (!linked || followTask?.rewardAmountCent == null || followTask.rewardAmountCent <= 0) {
    return undefined
  }

  return rewardAmountCentToCredits(followTask.rewardAmountCent)
}

function isBoundInBinds(binds: Record<string, unknown>, platform: SocialPlatform): boolean {
  const key = SOCIAL_BIND_KEYS[platform]
  return binds[key] === true
}

function getDevMockOverride(): Partial<Record<SocialPlatform, Partial<SocialBindStatus>>> | null {
  if (!import.meta.env.DEV) return null

  const param = new URLSearchParams(window.location.search).get('mockSocial')
  if (!param) return null

  const linkedMock = (accountName: string): Partial<SocialBindStatus> => ({
    linked: true,
    creditEarned: SOCIAL_MEDIA_MOCK_CREDIT_REWARD,
    accountName,
    avatarSeed: accountName,
  })

  if (param === 'all-linked') {
    return {
      x: linkedMock('AccountNameAccountNa'),
      discord: linkedMock('AccountNameAccountNa'),
    }
  }

  if (param === 'x-linked') {
    return { x: linkedMock('AccountNameAccountNa') }
  }

  if (param === 'discord-linked') {
    return { discord: linkedMock('AccountNameAccountNa') }
  }

  return null
}

function resolveLinkedAccount(
  platform: SocialPlatform,
  userName?: string,
): Pick<SocialBindStatus, 'accountName' | 'avatarSeed'> {
  const accountName = userName?.split('@')[0]
  if (!accountName) return {}

  return {
    accountName,
    avatarSeed: `${platform}-${accountName}`,
  }
}

export function buildSocialBindStatus(
  platform: SocialPlatform,
  binds: Record<string, unknown>,
  followTask?: FollowTaskInfo,
  mockOverride?: Partial<SocialBindStatus> | null,
  userName?: string,
): SocialBindStatus {
  if (mockOverride) {
    return {
      platform,
      linked: mockOverride.linked ?? false,
      accountName: mockOverride.accountName,
      avatarSeed: mockOverride.avatarSeed ?? mockOverride.accountName,
      creditEarned: mockOverride.creditEarned,
    }
  }

  const boundInBinds = isBoundInBinds(binds, platform)
  const linked = followTask?.bound ?? boundInBinds

  return {
    platform,
    linked,
    ...(linked ? resolveLinkedAccount(platform, userName) : {}),
    creditEarned: resolveCreditEarned(linked, followTask),
  }
}

export function getSocialBindStatuses(
  binds: Record<string, unknown>,
  followTasks: Partial<Record<SocialPlatform, FollowTaskInfo>> = {},
  userName?: string,
): SocialBindStatus[] {
  const mock = getDevMockOverride()

  return PLATFORMS.map((platform) =>
    buildSocialBindStatus(platform, binds, followTasks[platform], mock?.[platform], userName),
  )
}

export function getFollowTaskPlatform(platform: SocialPlatform): number | undefined {
  return FOLLOW_TASK_PLATFORM[platform]
}

async function fetchFollowTasks(): Promise<Partial<Record<SocialPlatform, FollowTaskInfo>>> {
  const followTasks: Partial<Record<SocialPlatform, FollowTaskInfo>> = {}

  for (const platform of PLATFORMS) {
    const followPlatform = getFollowTaskPlatform(platform)
    if (followPlatform == null) continue

    try {
      const res = await API.getFollowTask({ platform: followPlatform })
      if (res.code === 0) {
        followTasks[platform] = {
          bound: res.bound,
          followed: res.followed,
          rewardAmountCent: res.rewardAmount,
        }
      }
    } catch {
      // ignore: keep other platforms working if one follow_task fails
    }
  }

  return followTasks
}

/** Load latest binds + follow_task and build UI statuses. */
export async function fetchSocialBindStatuses(userName: string): Promise<SocialBindStatus[]> {
  let binds: Record<string, unknown> = {}

  try {
    const res = await API.getUserInfo()
    if (res.code === 0) {
      binds = (res.binds ?? {}) as Record<string, unknown>
    }
  } catch {
    // ignore: fall back to empty binds
  }

  const followTasks = await fetchFollowTasks()
  return getSocialBindStatuses(binds, followTasks, userName)
}

export function isPlatformLinked(statuses: SocialBindStatus[], platform: SocialPlatform): boolean {
  return statuses.find((s) => s.platform === platform)?.linked ?? false
}

function resolveRedirectUrl(location: string): string {
  if (/^https?:\/\//i.test(location)) {
    return location
  }

  const base = API_BASE_URL.startsWith('http')
    ? API_BASE_URL
    : `${window.location.origin}${API_BASE_URL}`

  return new URL(location, base.endsWith('/') ? base : `${base}/`).href
}

function openOAuthTab(url: string): void {
  // No 'noopener' in features: it forces window.open to return null even on
  // success, making popup-blocked detection impossible. Detach opener manually.
  const opened = window.open(resolveRedirectUrl(url), '_blank')
  if (!opened) {
    throw new Error('POPUP_BLOCKED')
  }
  opened.opener = null
}

// Social media binding is platform-only: the SocialMedia section is hidden on
// /team/profile, so binding always identifies the platform user.
function resolveOAuthBearer(): string {
  const token = getToken()
  if (!token) {
    throw new Error('NOT_LOGGED_IN')
  }
  return token.startsWith('Bearer ') ? token : `Bearer ${token}`
}

export async function startOAuthBindFollow(oauthType: string): Promise<void> {
  const token = resolveOAuthBearer()

  const params = new URLSearchParams({
    oauth_type: oauthType,
    purpose_type: 'bind_follow',
  })
  const requestUrl = `${API_BASE_URL}/oauth_user?${params.toString()}`

  const response = await fetch(requestUrl, {
    method: 'GET',
    headers: {
      Authorization: token,
      'Content-Type': 'application/json; charset=UTF-8',
    },
    credentials: 'include',
  })

  // Preferred: URL exposed via a readable response header.
  const headerUrl = response.headers.get('X-Oauth-Url') || response.headers.get('Location')
  if (headerUrl) {
    openOAuthTab(headerUrl)
    return
  }

  let payload: {
    code?: number
    msg?: string
    url?: string
    redirectUrl?: string
    location?: string
  } | null = null
  try {
    payload = await response.json()
  } catch {
    payload = null
  }

  const bodyUrl = payload?.url || payload?.redirectUrl || payload?.location
  if (bodyUrl) {
    openOAuthTab(bodyUrl)
    return
  }

  if (payload?.msg) {
    throw new Error(payload.msg)
  }

  throw new Error('OAUTH_START_FAILED')
}
